// Reusable documents panel for any capture entity. Upload a file (it links to
// the entity), classify it (document type + free-form label), open it, or
// remove the link. Mounted on the capture-level Documents tab and inside each
// entity's edit Sheet (RFI / interaction / workstream), so documents live on
// every capture surface.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraIconButton, SpecraInput } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { FileText, Paperclip, Upload, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { EmptyState } from '../Delivery/_shared/components/PageStates';
import { openArtifact, useClassifyArtifact, useEntityArtifacts, useUnlinkEntityArtifact, useUploadEntityDocument } from './documentHooks';
export const tesoroDocumentsBackend = {
    useList: useEntityArtifacts,
    useUpload: useUploadEntityDocument,
    useClassify: useClassifyArtifact,
    useUnlink: useUnlinkEntityArtifact
};
function ArtifactRow({ artifact, entityType, entityId, backend, onOpenDocument }) {
    const classify = backend.useClassify(entityType, entityId);
    const unlink = backend.useUnlink(entityType, entityId);
    const [editing, setEditing] = useState(false);
    const [docType, setDocType] = useState(artifact.documentType);
    const [label, setLabel] = useState(artifact.classificationLabel);
    const [confirmRemove, setConfirmRemove] = useState(false);
    return /*#__PURE__*/ _jsxs("div", {
        className: "rounded-md border p-3 text-sm",
        "data-testid": Testids.capture.docRow,
        "data-artifact-id": artifact.artifactId,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between gap-2",
                children: [
                    /*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        className: "flex min-w-0 items-center gap-2 text-left hover:underline",
                        onClick: ()=>onOpenDocument ? onOpenDocument(artifact) : openArtifact(artifact.artifactId),
                        "data-testid": onOpenDocument ? Testids.capture.docPreviewButton : Testids.capture.docOpenButton,
                        children: [
                            /*#__PURE__*/ _jsx(FileText, {
                                className: "h-4 w-4 shrink-0 text-muted-foreground"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "truncate",
                                children: artifact.filename
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex shrink-0 items-center gap-1",
                        children: [
                            artifact.classificationLabel || artifact.documentType ? /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: "neutral",
                                children: artifact.classificationLabel || artifact.documentType
                            }) : /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: "outline",
                                children: "Unclassified"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "ghost",
                                size: "sm",
                                "data-testid": Testids.capture.docClassifyButton,
                                onClick: ()=>setEditing((e)=>!e),
                                children: "Classify"
                            }),
                            confirmRemove ? /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "destructive",
                                        size: "sm",
                                        disabled: unlink.isPending,
                                        "data-testid": Testids.capture.docRemoveConfirm,
                                        onClick: ()=>unlink.mutate(artifact.artifactId, {
                                                onSuccess: ()=>setConfirmRemove(false)
                                            }),
                                        children: "Remove?"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "ghost",
                                        size: "sm",
                                        onClick: ()=>setConfirmRemove(false),
                                        children: "Cancel"
                                    })
                                ]
                            }) : /*#__PURE__*/ _jsx(SpecraIconButton, {
                                variant: "ghost",
                                size: "sm",
                                "data-testid": Testids.capture.docRemoveButton,
                                onClick: ()=>setConfirmRemove(true),
                                label: "Remove document",
                                children: /*#__PURE__*/ _jsx(X, {
                                    className: "h-4 w-4"
                                })
                            })
                        ]
                    })
                ]
            }),
            editing && /*#__PURE__*/ _jsxs("div", {
                className: "mt-2 flex flex-wrap items-end gap-2 border-t pt-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "Document type"
                            }),
                            /*#__PURE__*/ _jsx(SpecraInput, {
                                className: "h-8 w-[160px]",
                                value: docType,
                                onChange: (e)=>setDocType(e.target.value),
                                placeholder: "e.g. solicitation",
                                "data-testid": Testids.capture.docTypeInput
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex-1 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "Label"
                            }),
                            /*#__PURE__*/ _jsx(SpecraInput, {
                                className: "h-8",
                                value: label,
                                onChange: (e)=>setLabel(e.target.value),
                                placeholder: "Human-readable classification",
                                "data-testid": Testids.capture.docLabelInput
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        disabled: classify.isPending,
                        "data-testid": Testids.capture.docClassifySave,
                        onClick: ()=>classify.mutate({
                                artifactId: artifact.artifactId,
                                documentType: docType,
                                classificationLabel: label
                            }, {
                                onSuccess: ()=>setEditing(false)
                            }),
                        children: "Save"
                    })
                ]
            })
        ]
    });
}
export function DocumentsPanel({ entityType, entityId, title = 'Documents', backend = tesoroDocumentsBackend, onOpenDocument }) {
    const { data: artifacts, isLoading } = backend.useList(entityType, entityId);
    const upload = backend.useUpload(entityType, entityId);
    const fileRef = useRef(null);
    const onPick = (e)=>{
        const file = e.target.files?.[0];
        if (file) upload.mutate(file);
        e.target.value = ''; // allow re-selecting the same file
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-3",
        "data-testid": Testids.capture.documentsPanel,
        "data-entity": entityType,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-1.5 text-sm font-medium",
                        children: [
                            /*#__PURE__*/ _jsx(Paperclip, {
                                className: "h-4 w-4 text-muted-foreground"
                            }),
                            " ",
                            title
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        disabled: upload.isPending,
                        "data-testid": Testids.capture.docUploadButton,
                        onClick: ()=>fileRef.current?.click(),
                        iconLeft: /*#__PURE__*/ _jsx(Upload, {
                            className: "h-4 w-4"
                        }),
                        children: upload.isPending ? 'Uploading…' : 'Upload'
                    }),
                    /*#__PURE__*/ _jsx("input", {
                        ref: fileRef,
                        type: "file",
                        className: "hidden",
                        onChange: onPick,
                        "data-testid": Testids.capture.docFileInput
                    })
                ]
            }),
            isLoading ? /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "Loading documents…"
            }) : !artifacts || artifacts.length === 0 ? /*#__PURE__*/ _jsx(EmptyState, {
                icon: FileText,
                title: "No documents yet",
                description: "Upload solicitations, market research, intel, and supporting files here."
            }) : /*#__PURE__*/ _jsx("div", {
                className: "space-y-2",
                children: artifacts.map((a)=>/*#__PURE__*/ _jsx(ArtifactRow, {
                        artifact: a,
                        entityType: entityType,
                        entityId: entityId,
                        backend: backend,
                        onOpenDocument: onOpenDocument
                    }, a.artifactId))
            })
        ]
    });
}
